import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import Table from 'components/common/Table/Table';
import TableBody from 'components/common/Table/TableBody/TableBody';
import TableBodyCell from 'components/common/Table/TableBodyCell/TableBodyCell';
import TableHead from 'components/common/Table/TableHead/TableHead';
import TableHeadCell from 'components/common/Table/TableHeadCell/TableHeadCell';
import TableRow from 'components/common/Table/TableRow/TableRow';
import Tooltip from 'components/common/Tooltip/Tooltip';
import Checkbox from 'components/common/Checkbox/Checkbox';
import Button from 'components/common/Button/Button';
import { capitalize } from 'common/utils/string.utils';
import { CloseIcon, DoneDarkIcon, TooltipIcon } from 'components/common/Icons/Icons';
import { RootStoreContext } from 'index';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import {
  EMAIL_EVENTS_TOOLTIPS,
  MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT,
  STATUS_LABELS,
} from 'common/constants/emailNotifications.constants';
import { StatusLabel } from 'common/enums/emailNotifications.enums';

import styles from './UserEmailNotificationsTable.module.scss';
import { getCheckedEvents } from '../userEmailNotifications.utils';

export interface EmailNotificationsTableProps {
  isEdit: boolean;
  handleCancelEdit: () => void;
}

const UserEmailNotificationsTable: React.FC<EmailNotificationsTableProps> = ({ isEdit, handleCancelEdit }) => {
  const { authStore, userEmailNotificationsStore } = useContext(RootStoreContext);
  const { account } = authStore;
  const { getNotifications, notifications, isLoading } = userEmailNotificationsStore;
  const email = account?.email;

  const originalEvents = getCheckedEvents(notifications?.events);

  const [checkedEvents, setCheckedEvents] = useState<Record<StatusLabel, boolean>>(originalEvents);

  const fetchNotifications = async () => {
    try {
      await getNotifications();
    } catch (error: any) {
      notify(error.message, 'error');
    }
  };

  useEffect(() => {
    if (!authStore.isAuthenticated) {
      return;
    }

    fetchNotifications();
  }, [getNotifications]);

  useEffect(() => {
    setCheckedEvents(getCheckedEvents(notifications?.events));
  }, [notifications]);

  const handleCheckboxChange = (status: StatusLabel, value: boolean) => {
    setCheckedEvents((prev) => ({ ...prev, [status]: value }));
  };

  const handleSave = async () => {
    const enabledEvents = STATUS_LABELS.filter((status) => checkedEvents[status]).map(
      (status) => MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT[status],
    );

    try {
      if (notifications && notifications.id) {
        await userEmailNotificationsStore.updateNotifications(enabledEvents);
        notify(ToastMessage.NotificationsUpdated, 'success');
      } else {
        await userEmailNotificationsStore.createNotifications(enabledEvents);
        notify(ToastMessage.NotificationsCreate, 'success');
      }

      handleCancelEdit();
    } catch (error: any) {
      notify(error.message || 'Failed to save notification settings', 'error');
    }
  };

  const renderCellIcon = (isChecked: boolean, editable: boolean, status: StatusLabel) => {
    if (editable) {
      return (
        <Checkbox isChecked={isChecked} onChange={(event) => handleCheckboxChange(status, event.target.checked)} />
      );
    }

    return isChecked ? <DoneDarkIcon /> : <CloseIcon />;
  };

  return (
    <>
      <Table className={styles.emailSettingsTable}>
        <TableHead className={styles.tableRowTemplate}>
          <TableHeadCell isSorted={false} currentSort={false}>
            Email
          </TableHeadCell>
          {STATUS_LABELS.map((title) => (
            <TableHeadCell key={title} isSorted={false} currentSort={false}>
              <div className={styles.tableHeaderCell}>
                {capitalize(title)}
                <Tooltip title={EMAIL_EVENTS_TOOLTIPS[title]}>
                  <TooltipIcon />
                </Tooltip>
              </div>
            </TableHeadCell>
          ))}
        </TableHead>
        <TableBody>
          <TableRow className={styles.tableRowTemplate}>
            <TableBodyCell className={styles.bodyCell}>
              <span>{email}</span>
            </TableBodyCell>
            {STATUS_LABELS.map((status) => (
              <TableBodyCell key={status} className={styles.bodyCell}>
                {renderCellIcon(checkedEvents[status], isEdit, status as StatusLabel)}
              </TableBodyCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      {isEdit && (
        <div className={styles.editButtons}>
          <Button onClick={handleSave} isLoading={isLoading}>
            Save Changes
          </Button>
          <Button
            variant="white"
            onClick={() => {
              handleCancelEdit();
              setCheckedEvents(originalEvents);
            }}
            isLoading={isLoading}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );
};

export default observer(UserEmailNotificationsTable);
